import React, { useState, useMemo } from 'react';
import { TextField, Tooltip } from '@mui/material';
import constants from '../../../../constants';

const NodeLibrary = ({
  nodeTemplates = {},
  onNodeSelect,
  readOnly = false,
  searchable = true,
  compact = false,
  onDragStart = null,
  activeType = null,
  onToggleType = null
}) => {
  const [searchText, setSearchText] = useState('');
  const [localActiveType, setLocalActiveType] = useState(null);

  // Use provided activeType and onToggleType if available, otherwise use local state
  const currentActiveType = activeType != null ? activeType : localActiveType;
  const handleToggle = onToggleType || ((type) => {
    setLocalActiveType(prev => prev === type ? null : type);
  });

  // Filter nodes based on search
  const filteredTemplates = useMemo(() => {
    if (!searchText.trim()) return nodeTemplates;

    const searchLower = searchText.toLowerCase();
    return Object.entries(nodeTemplates).reduce((acc, [type, config]) => {
      if (
        config.label.toLowerCase().includes(searchLower) ||
        config.description.toLowerCase().includes(searchLower) ||
        type.toLowerCase().includes(searchLower)
      ) {
        acc[type] = config;
      }
      return acc;
    }, {});
  }, [nodeTemplates, searchText]);

  const handleNodeClick = (type, customConfig = {}) => {
    if (onNodeSelect) {
      onNodeSelect(type, customConfig);
    }
  };

  return (
    <div className={compact ? 'node-library node-library-compact' : 'node-library'} key={`node-library-${currentActiveType}`}>
      {searchable && (
        <div className='searchbox'>
          <TextField
            fullWidth
            hiddenLabel
            size="small"
            name='search'
            variant="standard"
            placeholder='Search node...'
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <i className='icon'>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5.82319 10.6464C3.16455 10.6464 1 8.48183 1 5.82319C1 3.16455 3.16455 1 5.82319 1C8.48183 1 10.6464 3.16455 10.6464 5.82319C10.6464 8.48183 8.48183 10.6464 5.82319 10.6464ZM5.82319 1.70583C3.55041 1.70583 1.70583 3.55511 1.70583 5.82319C1.70583 8.09127 3.55041 9.94055 5.82319 9.94055C8.09597 9.94055 9.94055 8.09127 9.94055 5.82319C9.94055 3.55511 8.09597 1.70583 5.82319 1.70583Z" fill="#505258" stroke="#505258" strokeWidth="0.236842" />
              <path d="M13.3182 13.7068C13.2245 13.7069 13.1307 13.6725 13.0566 13.5985L9.10425 9.63167C8.96097 9.48877 8.96065 9.25192 9.10355 9.10864C9.24646 8.96536 9.4833 8.96505 9.62658 9.10795L13.5789 13.0748C13.7222 13.2177 13.7225 13.4546 13.5796 13.5979C13.5057 13.672 13.412 13.7066 13.3182 13.7068Z" fill="#505258" stroke="#505258" strokeWidth="0.236842" />
            </svg>
          </i>
        </div>
      )}

      <div className={compact ? 'node-list-compact' : 'drag-list'}
        onWheel={(e) => {
          // Prevent wheel event from bubbling up to React Flow canvas
          e.stopPropagation();
        }}>
        {Object.entries(filteredTemplates).map(([type, config]) => (
          <div key={type} className={compact ? 'node-category-compact' : ''} >
            {/* Node Type Header */}
            <div
              className={`list-category-header ${currentActiveType === type ? "active" : ""}`}
              onClick={() => handleToggle(type)}
              style={{ cursor: 'pointer' }}
            >
              <div className='icon-box' style={{ backgroundColor: config.color }}>
                {config.icon}
              </div>
              <div className='text-area'>
                <h5>{config.label}</h5>
                {/* <p>{config.description}</p> */}
              </div>
              <span className="arrow-right" style={{
                transform: currentActiveType === type ? 'rotate(90deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }}>
                <svg width="6" height="11" viewBox="0 0 6 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0.3125 4.52539C-0.00427618 4.8422 -0.00429678 5.36777 0.3125 5.68457L4.52539 9.89746C4.8422 10.2142 5.36779 10.2142 5.68457 9.89746C5.84878 9.73325 5.9248 9.52532 5.9248 9.31738C5.92471 9.10961 5.84862 8.90238 5.68457 8.73828L2.05078 5.10449L2.10449 5.05176L5.68457 1.47168C6.00134 1.15488 6.00134 0.6293 5.68457 0.3125C5.36777 -0.0042967 4.8422 -0.00427595 4.52539 0.3125L0.3125 4.52539Z" fill="#4B5563" stroke="white" strokeWidth="0.15" />
                </svg>
              </span>
            </div>

            {/* Expanded Node Options */}
            <div className="submenu-node" style={{ display: currentActiveType === type ? 'block' : 'none' }}>
              {/* Default Configuration Option */}
              <div
                className='list-item list-sub-item'
                draggable={!readOnly && onDragStart ? "true" : "false"}
                onDragStart={onDragStart ? (event) => onDragStart(event, type) : undefined}
                onClick={!onDragStart ? (e) => {
                  e.stopPropagation();
                  handleNodeClick(type);
                } : undefined}
                style={{ cursor: onDragStart ? 'grab' : 'pointer' }}
              >
                <div className='icon-box-small'>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 2V14M2 8H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </div>
                <div className='text-area'>
                  <h6>Default Configuration</h6>
                  <p>Standard {config.label.toLowerCase()} node</p>
                </div>
              </div>

              {/* Template Options */}
              {(config.template?.nodeDefinition?.options || []).map((option, index) => (
                <div
                  key={option.value || index}
                  className='list-item list-sub-item'
                  draggable={!readOnly && onDragStart ? "true" : "false"}
                  onDragStart={onDragStart ? (event) => {
                    onDragStart(event, type);
                    // Pass custom config in dataTransfer
                    event.dataTransfer.setData('customConfig', JSON.stringify(option.config || {}));
                  } : undefined}
                  onClick={!onDragStart ? (e) => {
                    e.stopPropagation();
                    handleNodeClick(type, option.config || {});
                  } : undefined}
                  style={{ cursor: onDragStart ? 'grab' : 'pointer' }}
                >
                  <div className='icon-box-small'>
                    {constants.getIconComponent(option.icon)}
                  </div>
                  <div className='text-area'>
                    <h6>{option.label}</h6>
                    {option.description && <p>{option.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Empty State */}
        {Object.keys(filteredTemplates).length === 0 && (
          <div className='empty-state' style={{ textAlign: 'center', padding: '20px', opacity: 0.7 }}>
            {searchText ? (
              <p>No nodes found matching "{searchText}"</p>
            ) : (
              <p>No nodes available</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NodeLibrary;